import React, { useContext } from 'react'
import { sessionContext, useGetBusiness } from '@marketing-milk/frontend'
import { SurfacePalette } from '../../tokens'
import { useSprint } from 'hooks/api/useSprint'
import { AdCreative, Permissions } from '@marketing-milk/interfaces'
import { creativeWizardUrl } from '../../../creatives/creativeUrl'
import { SectionCard } from './SectionCard'
import { CreativeCardList } from './CreativeCardList'
import { useCreativeApproval } from './useCreativeApproval'
import { useCreativePreview } from './useCreativePreview'
import { useRefreshVideoAssets } from './useRefreshVideoAssets'

interface Props {
  palette: SurfacePalette
  packetID: number
  sprintID?: number
}

/**
 * Edit-mode Creative section: the section chrome keeps its packet-local
 * `section` anchor (`<packetID>:creative`), while each rendered creative card
 * carries its own `creative` anchor (see `CreativeCardList`). Any pre-published
 * creative that isn't already Approved exposes an Approve action that records an
 * AE-attested client approval; Approved creatives expose a Remove-approval action.
 * Both flow through the shared creative review system.
 */
export const CreativeSection = ({ palette, packetID, sprintID }: Props) => {
  const business = useGetBusiness()
  const { user } = useContext(sessionContext)
  const { sprintCreatives, sprintCreativesLoading, refetchCreatives } = useSprint(sprintID)
  const { onApprove, onRemoveApproval, modals } = useCreativeApproval(business.id, palette)

  // Internal-only recovery action: re-sync ad videos + creatives from Facebook to
  // repair missing video relations/URLs. Hidden from client/present view; server
  // still enforces business access on the sync endpoints regardless.
  const isStaff = !!user?.permissions.includes(Permissions.AccessAdminApp)
  const { refresh, isRefreshing } = useRefreshVideoAssets(business.id, {
    refetchCreatives,
    showAuthorizerLink: isStaff,
  })

  // Edit-only: source edits (typos, copy) belong at the creative, not the packet.
  const onOpenSource = (creative: AdCreative) =>
    window.open(creativeWizardUrl(business.id, creative.id), '_blank', 'noopener')

  // Approve / Remove / Open-source live on the preview (the only surface showing
  // the full ad copy), not on the card.
  const {
    onPreview,
    prefetch,
    modals: previewModals,
  } = useCreativePreview(
    palette,
    undefined,
    {
      onApprove,
      onRemoveApproval,
      onOpenSource,
    },
    { creatives: sprintCreatives, refetchCreatives }
  )

  return (
    <SectionCard
      palette={palette}
      sectionKey="creative"
      title="Creative"
      commentAnchor={{ type: 'section', id: `${packetID}:creative`, label: 'Creative' }}
    >
      <CreativeCardList
        palette={palette}
        creatives={sprintCreatives}
        loading={sprintCreativesLoading}
        noSprint={!sprintID}
        onPreview={onPreview}
        onPrefetch={prefetch}
      />
      {isStaff && sprintID && (
        <button
          type="button"
          onClick={refresh}
          disabled={isRefreshing}
          style={{
            marginTop: 12,
            border: 'none',
            background: 'transparent',
            color: palette.textMuted,
            fontSize: 12,
            cursor: isRefreshing ? 'default' : 'pointer',
            opacity: isRefreshing ? 0.6 : 1,
            padding: 0,
          }}
        >
          <i className={`fa fa-${isRefreshing ? 'spinner fa-spin' : 'refresh'} mr-2`} />
          {isRefreshing ? 'Refreshing video assets…' : 'Refresh video assets'}
        </button>
      )}
      {modals}
      {previewModals}
    </SectionCard>
  )
}
